/**
 * A one-line notice across the top of the app, set from the server.
 *
 * Josh, 2026-10-22, during the Railway outage: "something like some type of an
 * alert that says the website host has experienced some outages so there may be
 * some slight situations where things aren't working right."
 *
 * ⚠️ THE TEXT COMES FROM THE SERVER, NOT FROM THIS FILE. `SITE_NOTICE` is a
 * runtime variable on the API, so turning this on or off is a variable change
 * and a deploy — no code edit, no rebuild, and nobody has to wait for a
 * developer. Baking the words in here, or into a VITE_ variable, would mean the
 * notice outlived the problem it described because clearing it was a chore.
 *
 * ⚠️ AND IT IS READ FROM /api/health, WHICH IS ALREADY PUBLIC AND UNAUTHENTICATED.
 * The one moment you most want to tell people something is when other things are
 * failing — a notice that needs a session to fetch is a notice that disappears
 * exactly when it is needed.
 */
import { useEffect, useState } from 'react';

const BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Dismissal is keyed to the text itself.
 *
 * ⚠️ SO A NEW NOTICE SHOWS AGAIN TO SOMEBODY WHO DISMISSED THE LAST ONE. Keying
 * it to a fixed string would mean the first person to close a notice never sees
 * another one, which turns this into a channel nobody receives.
 */
const keyFor = (text: string): string => {
  let h = 0;
  for (let i = 0; i < text.length; i++) h = (Math.imul(31, h) + text.charCodeAt(i)) | 0;
  return `site-notice:${h}`;
};

export function SiteNotice(): JSX.Element | null {
  const [text, setText] = useState('');

  useEffect(() => {
    let live = true;
    fetch(`${BASE}/api/health`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { notice?: string } | null) => {
        if (!live) return;
        const notice = String(d?.notice ?? '').trim();
        if (!notice) return;
        try {
          if (localStorage.getItem(keyFor(notice))) return;
        } catch {
          // A browser refusing storage just means it cannot be dismissed. Showing
          // it is the right way to fail: the point of a notice is being seen.
        }
        setText(notice);
      })
      .catch(() => {
        /*
          ⚠️ SILENT ON FAILURE, DELIBERATELY. If the API is unreachable the user
          already knows something is wrong, and a banner saying "could not load
          the banner" is noise on top of an outage.
        */
      });
    return () => {
      live = false;
    };
  }, []);

  if (!text) return null;

  return (
    <div className="site-notice" role="status">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 3a9 9 0 100 18 9 9 0 000-18zM12 8v5M12 16h.01" />
      </svg>
      <p>{text}</p>
      <button
        type="button"
        aria-label="Dismiss this notice"
        onClick={() => {
          try {
            localStorage.setItem(keyFor(text), '1');
          } catch {
            // Dismissing without storage lasts until reload. Acceptable.
          }
          setText('');
        }}
      >
        ×
      </button>
    </div>
  );
}
